// src/components/landing/LegalPage.js
import React, { useState, useEffect } from 'react';
import './LegalPage.css';

// Componente `BackArrowIcon` que renderiza esta parte de la interfaz.
const BackArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"></line>
    <polyline points="12 19 5 12 12 5"></polyline>
  </svg>
);

// Componente `ShieldIcon` que renderiza esta parte de la interfaz.
const ShieldIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
);

// Componente `DocumentIcon` que renderiza esta parte de la interfaz.
const DocumentIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

// Componente `MailIcon` que renderiza esta parte de la interfaz.
const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
    <polyline points="22,6 12,13 2,6"></polyline>
  </svg>
);

// Componente `GlobeIcon` que renderiza esta parte de la interfaz.
const GlobeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

// Componente `LegalPage` que renderiza esta parte de la interfaz.
export default function LegalPage({ onNavigate, initialTab = 'terms', t = {} }) {
  // Estado local `activeTab` y su actualizador, usados para reflejar cambios en la interfaz.
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  // Función `handleGoBack` que gestiona la interacción o el evento correspondiente.
  const handleGoBack = () => {
    if (onNavigate) {
      onNavigate('registrate');
    }
  };

  // Contenido de Términos y Condiciones
  const termsContent = {
    title: "Términos y Condiciones",
    lastUpdate: "Vigente",
    companyInfo: {
      name: "Impulsora Mexicana de Derecho y Tecnología, S.A. de C.V.",
      email: "contacto@wisby.legal",
      website: "www.wisby.legal"
    },
    sections: [
      {
        title: "Aceptación de los Términos",
        content: `El acceso, navegación y uso del sitio web Wisby (en adelante, el "Sitio") propiedad de Impulsora Mexicana de Derecho y Tecnología, S.A. de C.V. (en lo sucesivo, la "Empresa"), así como la contratación de cualquier producto o servicio ofrecido por la Empresa, implican la aceptación expresa, plena y sin reservas de los presentes Términos y Condiciones de Uso.

Si el Usuario o Cliente no está de acuerdo con estos Términos, deberá abstenerse de utilizar el Sitio, sus contenidos o servicios.`
      },
      {
        title: "Identificación del Titular",
        content: `Titular: Impulsora Mexicana de Derecho y Tecnología, S.A. de C.V.`,
        listItems: [
          "Correo electrónico: contacto@wisby.legal",
          "Sitio web: www.wisby.legal"],
        additionalContent: "Los presentes Términos son aplicables a la Empresa y a todas sus marcas, unidades operativas, productos, servicios, plataformas y dominios digitales administrados directa o indirectamente.",
        isContactInfo: true
      },
      {
        title: "Objeto",
        content: `Estos términos y condiciones regulan el acceso y uso del Sitio, así como las condiciones generales bajo las cuales la Empresa pone a disposición de los Usuarios contenidos, herramientas, productos digitales y servicios profesionales.`
      },
      {
        title: "Uso autorizado del Sitio",
        content: `El Usuario se obliga a utilizar el Sitio de manera lícita, responsable y conforme a la legislación mexicana vigente.

Queda estrictamente prohibido:`,
        listItems: [
          "Utilizar el Sitio con fines ilícitos, fraudulentos o contrarios a la ley.",
          "Introducir virus, malware, bots o procesos automatizados no autorizados.",
          "Reproducir, copiar, distribuir o modificar contenidos sin autorización expresa.",
          "Proporcionar información falsa o suplantar identidades.",
          "Interferir o dañar la infraestructura técnica del Sitio."
        ],
        additionalContent: `El incumplimiento podrá derivar en la suspensión inmediata del acceso y en las acciones legales correspondientes.`
      },
      {
        title: "Acceso y disponibilidad",
        content: `El acceso al Sitio es gratuito, sin perjuicio de que determinados servicios, productos o contenidos estén sujetos a registro, condiciones particulares o pago.

La Empresa se reserva el derecho de modificar, suspender o descontinuar, total o parcialmente, el Sitio o cualquiera de sus funcionalidades por razones técnicas, operativas, comerciales o de seguridad.`
      },
      {
        title: "Registro y contratación de servicios",
        content: `Para contratar servicios o adquirir productos, el Usuario deberá proporcionar información veraz, actualizada y completa mediante los medios habilitados.

La contratación se considerará formalizada únicamente cuando:`,
        listItems: [
          "El Usuario confirme su solicitud.",
          "Las partes ejecuten un contrato formal.",
          "Se realice el pago correspondiente, en su caso.",
          "La Empresa emita confirmación por correo electrónico o medio digital equivalente."
        ],
        additionalContent: `Toda contratación implica la aceptación de estos términos y condiciones, del Aviso Legal, del Aviso de Privacidad y de las políticas específicas aplicables.`
      },
      {
        title: "Naturaleza de los servicios",
        content: `Los servicios ofrecidos por la Empresa son de carácter profesional, estratégico y consultivo. La información, diagnósticos y recomendaciones proporcionadas no constituyen garantías de resultados ni sustituyen asesoría especializada sin contrato formal.`
      },
      {
        title: "Propiedad intelectual e industrial",
        content: `Todos los contenidos del Sitio, incluyendo textos, metodologías, documentos, marcas, logotipos y materiales, son propiedad de la Empresa o de terceros autorizados y están protegidos por la legislación aplicable.

Queda prohibida cualquier forma de explotación no autorizada.`
      },
      {
        title: "Licencias de uso",
        content: `La adquisición de productos digitales otorga únicamente una licencia de uso personal, limitada, no exclusiva y no transferible. Se prohíbe su reventa, redistribución o uso con fines de competencia.`
      },
      {
        title: "Precios y pagos",
        content: `Los precios se expresan en pesos mexicanos (MXN) e incluyen los impuestos aplicables. Los pagos se procesan mediante plataformas de terceros certificadas.

La Empresa no almacena datos financieros sensibles y podrá cancelar operaciones ante indicios razonables de fraude.`
      },
      {
        title: "Limitación de responsabilidad",
        content: `El Sitio, así como sus contenidos y servicios, se ponen a disposición del usuario en el estado en que se encuentran y conforme a su disponibilidad. La Empresa no garantiza resultados específicos ni asume responsabilidad por decisiones tomadas por el Usuario con base en la información proporcionada.`
      },
      {
        title: "Datos personales",
        content: `El tratamiento de datos personales se rige por el Aviso de Privacidad y la legislación aplicable vigente, elaborados conforme a la normativa aplicable.`
      },
      {
        title: "Enlaces a terceros",
        content: `El Sitio puede contener enlaces a sitios de terceros. La Empresa no controla ni asume responsabilidad por su contenido, disponibilidad o prácticas.`
      },
      {
        title: "Tecnologías y herramientas digitales",
        content: `El Sitio utiliza herramientas tecnológicas de terceros para fines operativos, analíticos, de seguridad y mejora del servicio, conforme a la Política de Cookies vigente.`
      },
      {
        title: "Suspensión y terminación",
        content: `La Empresa podrá suspender o cancelar el acceso al Sitio o a los servicios ante incumplimientos de estos Términos, sin derecho a indemnización, salvo disposición expresa en políticas aplicables o contratos firmados.`
      },
      {
        title: "Prelación normativa",
        content: `En caso de contradicción entre lo establecido aquí y otros documentos legales, prevalecerán en el siguiente orden:`,
        orderedList: [
          "Contratos firmados entre las partes.",
          "Avisos y políticas específicas del servicio.",
          "Los presentes Términos y Condiciones.",
          "El Aviso de Privacidad."
        ]
      },
      {
        title: "Modificaciones",
        content: `La Empresa se reserva el derecho de modificar estos Términos en cualquier momento. La versión vigente será la publicada en el Sitio.`
      },
      {
        title: "Ley aplicable y jurisdicción",
        content: `Estos Términos se rigen por las leyes de los Estados Unidos Mexicanos. Para cualquier controversia, las partes se someten a los tribunales competentes de Monterrey, Nuevo León.`
      },
      {
        title: "Contacto",
        content: `Correo electrónico: contacto@wisby.legal`,
        isContactSection: true
      }
    ]
  };

  // Contenido de Aviso de Privacidad
  const privacyContent = {
    title: "Aviso de Privacidad",
    lastUpdate: "Vigente",
    companyInfo: {
      name: "Impulsora Mexicana de Derecho y Tecnología, S.A. de C.V.",
      email: "contacto@wisby.legal",
      website: "www.wisby.legal"
    },
    introText: `Impulsora Mexicana de Derecho y Tecnología, S.A. de C.V. (en lo sucesivo la "Empresa"), hace de su conocimiento que, en cumplimiento con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares, su Reglamento y demás disposiciones aplicables (la "Legislación"), son responsables del uso y protección de los datos personales que usted nos proporcione, y al respecto informamos lo siguiente:`,
    sections: [
      {
        title: "1. Datos Personales que recabamos",
        content: `Podremos obtener los siguientes datos personales de manera directa, indirecta o a través de nuestras plataformas digitales:`,
        dataCategories: [
          {
            category: "Identificación y contacto",
            items: "Nombre completo, domicilio, edad, fecha de nacimiento, género, estado civil, CURP y RFC."
          },
          {
            category: "Contacto",
            items: "Correo electrónico y teléfono."
          },
          {
            category: "Datos laborales y empresariales",
            items: "Giro, tamaño de la empresa, número de colaboradores y necesidades de consultoría."
          },
          {
            category: "Financieros",
            items: "Información bancaria y cuentas de pago."
          },
          {
            category: "Datos sensibles (cuando aplique)",
            items: "Información biométrica u otros, mismos que serán tratados bajo las más estrictas medidas de seguridad y confidencialidad."
          }
        ],
        additionalContent: `El suministro de sus datos es voluntario; sin embargo, en caso de no proporcionarlos, es posible que no podamos cumplir con las finalidades descritas en este Aviso.`
      },
      {
        title: "2. Finalidades del Tratamiento",
        content: `Los datos personales recabados serán utilizados para las siguientes finalidades primarias, que son necesarias para la existencia y cumplimiento de la relación jurídica con las Empresas:`,
        listItems: [
          "Atender solicitudes de información, diagnósticos, asesoría o cotizaciones.",
          "Prestar servicios profesionales de consultoría, capacitación y acompañamiento.",
          "Formalizar relaciones comerciales, contractuales y administrativas.",
          "Emitir facturación, comprobantes fiscales y cumplir obligaciones legales.",
          "Dar seguimiento operativo, administrativo y de calidad a los servicios.",
          "Garantizar la seguridad, integridad y continuidad de los servicios.",
          "Adicionalmente, podremos utilizar sus datos personales para las siguientes:"
        ],
        subSection: {
          title: "Finalidades secundarias:",
          listItems: [
            "Ofrecer información promocional, publicitaria o de mercadotecnia relacionada con nuestros productos y servicios.",
            "Elaborar perfiles de consumo y realizar estudios de mercado.",
            "Invitarlo a participar en encuestas, concursos, promociones y programas de lealtad.",
            "Evaluar la calidad en el servicio y la experiencia del cliente."
          ]
        },
        additionalContent: `En caso de que no desee que sus datos sean tratados para finalidades secundarias, podrá manifestarlo conforme a lo establecido en la sección 9 de este Aviso.`
      },
      {
        title: "3. Transferencia de Datos Personales",
        content: `Las Empresas podrán transferir sus datos personales, dentro y fuera de México, en los siguientes supuestos:`,
        listItems: [
          "A sociedades subsidiarias, filiales, controladoras o afiliadas bajo el control común de las Empresas, o a cualquier sociedad del mismo grupo corporativo.",
          "A terceros proveedores de servicios con los que tengamos relación contractual y que sean necesarios para las finalidades descritas.",
          "A autoridades competentes en los casos legalmente previstos o requeridos por mandamiento judicial."
        ],
        additionalContent: `En todos los casos, las Empresas garantizarán que los terceros receptores asuman las mismas obligaciones de seguridad, privacidad y confidencialidad que correspondan conforme a la Legislación.`
      },
      {
        title: "4. Uso de Tecnologías de Rastreo",
        content: `Nuestras plataformas electrónicas utilizan cookies, web beacons y tecnologías similares para obtener información como: tipo de navegador, sistema operativo, páginas visitadas, dirección IP, duración en el sitio y hábitos de navegación. Estos datos se emplean con fines estadísticos, de mejora en la experiencia del usuario y de mercadotecnia.

Usted puede deshabilitar estas tecnologías desde la configuración de su navegador; sin embargo, algunas funcionalidades del sitio podrían no estar disponibles. Con el uso de la configuración de "cookies" en nuestro sitio web, plataformas y aplicaciones oficiales no se eliminarán "cookies" que ya se hayan configurado anteriormente. Si desea hacerlo, puede realizarlo reconfigurando su navegador web, después de haber cambiado la configuración de "cookies" de nuestro sitio web, plataformas y aplicaciones oficiales.

Adicionalmente, si ingresa mediante un dispositivo móvil a cualquiera de nuestras plataformas, aplicaciones y/o páginas de Internet oficiales, tendremos acceso al identificador alfanumérico único asignado a dicho dispositivo (este identificador también es conocido como "Device ID" o "Identificador Móvil"). Mediante este identificador podemos analizar y seguir el uso, ubicación y comportamiento en línea de dicho dispositivo, sin embargo, la Empresa lo utiliza únicamente para fines de publicidad y mercadotecnia. Si desea no otorgarnos —o dejar de otorgarnos— acceso a dicho identificador, puede hacerlo en cualquier momento directamente desde la configuración del dispositivo móvil en cuestión.`
      },
      {
        title: "5. Medidas de Seguridad",
        content: `La Empresa ha adoptado medidas de seguridad administrativas, técnicas y físicas que permiten proteger sus datos personales contra daño, pérdida, alteración, destrucción o uso indebido, acceso o divulgación no autorizados. Únicamente el personal autorizado tendrá acceso a sus datos, bajo estricta confidencialidad.`
      },
      {
        title: "6. Plazos de Conservación de Datos Personales",
        content: `Se conservarán los Datos Personales por el tiempo que nos sea legalmente permitido, mientras sean necesarios para dar cumplimiento a alguna de las finalidades establecidas en el presente Aviso o a algún requerimiento u obligación legal que nos sea aplicable, o bien, mientras sea permitido en atención a cualquier plazo de prescripción legal. Tomaremos las medidas necesarias para destruir o disociar los Datos Personales que estén en nuestra posesión cuando ya no sean necesarios para cumplir con las finalidades previstas anteriormente o una vez concluidos los plazos de retención.`
      },
      {
        title: "7. Tratamiento de Datos Personales de un Tercero Relacionado",
        content: `En caso de que, con motivo de dar cumplimiento a las finalidades señaladas anteriormente, la Empresa realice el tratamiento de los Datos Personales de terceros relacionados (tales como pareja o cónyuge, hijos, dependientes y/o referencias personales; cada uno de ellos un "Tercero Relacionado"), tiene la obligación de compartir el contenido del presente Aviso de Privacidad con dicho Tercero Relacionado y de obtener su consentimiento para que las Empresas puedan realizar el Tratamiento y hacer uso (incluyendo transmisión y revelación) de sus Datos Personales, de acuerdo a las finalidades previstas en el presente Aviso.

En caso de que no proporcione Datos Personales de Tercero Relacionado, implica que tiene su consentimiento y ha puesto a su disposición el presente Aviso de Privacidad en términos de lo señalado anteriormente, por lo que será su obligación sacar en paz y a salvo a la Empresa de toda responsabilidad que derive de cualquier queja o reclamación que llegase a imponer dicho Tercero Relacionado en nuestra contra.`
      },
      {
        title: "8. Tratamiento de Datos Personales de menores de edad",
        content: `Nuestros sitios web, plataformas y aplicaciones oficiales no están diseñados para ser utilizados por personas menores de edad, por lo que no recolectamos Datos Personales de menores de 18 años.`
      },
      {
        title: "9. Derechos ARCO y Revocación del Consentimiento",
        content: `Usted tiene derecho a Acceder, Rectificar, Cancelar u Oponerse al tratamiento de sus datos personales, así como a revocar el consentimiento otorgado para dicho tratamiento (derechos ARCO).

Para ejercer sus derechos, deberá presentar la solicitud correspondiente al correo electrónico:`,
        contactEmail: "contacto@wisby.legal",
        subSection: {
          title: "La solicitud deberá contener:",
          listItems: [
            "Nombre completo y medio para recibir notificaciones.",
            "Documentos que acrediten su identidad o representación legal.",
            "Descripción clara de los datos personales respecto de los cuales desea ejercer derechos.",
            "Cualquier otro elemento que facilite la localización de los datos."
          ]
        },
        additionalContent: `El plazo de respuesta será de 20 días hábiles contados desde la recepción de la solicitud.`
      },
      {
        title: "10. Cambios al Aviso de Privacidad",
        content: `El presente Aviso de privacidad puede sufrir modificaciones, cambios o actualizaciones derivadas de nuevos requerimientos legales y nuestras prácticas de privacidad, de nuestras propias necesidades por los productos o servicios que ofrecemos o por cualquier otra causa.

La Empresa podrá modificar o actualizar este Aviso en cualquier momento. Las modificaciones estarán disponibles en nuestro sitio web oficial: www.wisby.legal

La Empresa no será responsable si un Titular no recibe la notificación de cambio en el Aviso si existiere algún problema con su cuenta de correo electrónico, de transmisión de datos por Internet o si no se tuvo acceso a la página de Internet, plataforma o aplicación mencionada anteriormente por cualquier causa no imputable a la Empresa.`
      },
      {
        title: "11. Consentimiento",
        content: `Al proporcionar sus datos personales por cualquier medio, usted declara haber leído y entendido el presente Aviso de Privacidad, y otorga su consentimiento expreso para el tratamiento de los mismos en los términos aquí señalados.`
      },
      {
        title: "12. Contacto",
        content: `Si desea ejercer cualquiera de sus derechos, tiene alguna queja sobre la forma en la que sus Datos Personales son tratados o sobre nuestras prácticas en materia de privacidad y protección de datos personales, puede contactarnos mediante la dirección de correo electrónico:`,
        contactEmail: "contacto@wisby.legal",
        additionalContent: `Asimismo, recordamos el derecho que le asiste para acudir ante las autoridades competentes en materia de Datos Personales en caso de considerar que hubo vulneración o presume alguna violación a las disposiciones previstas en la normativa aplicable.`,
        isContactSection: true
      }
    ]
  };

  // Valor intermedio `currentContent` utilizado por la lógica de este bloque.
  const currentContent = activeTab === 'terms' ? termsContent : privacyContent;

  // Función para renderizar secciones con diferentes tipos de contenido
  const renderSection = (section, index) => {
    return (
      <div key={index} className="legal-section">
        <h2>{section.title}</h2>
        <div className="legal-section-content">
          {/* Contenido principal */}
          {section.content && (
            <div className="legal-text">
              {section.content.split('\n\n').map((paragraph, pIndex) => (
                <p key={pIndex}>{paragraph}</p>
              ))}
            </div>
          )}

          {/* Categorías de datos (para Aviso de Privacidad) */}
          {section.dataCategories && (
            <div className="legal-data-categories">
              {section.dataCategories.map((cat, catIndex) => (
                <div key={catIndex} className="legal-data-category">
                  <span className="category-name">{cat.category}:</span>
                  <span className="category-items">{cat.items}</span>
                </div>
              ))}
            </div>
          )}

          {/* Lista de items */}
          {section.listItems && (
            <ul className="legal-list">
              {section.listItems.map((item, itemIndex) => (
                <li key={itemIndex}>{item}</li>
              ))}
            </ul>
          )}

          {/* Lista ordenada */}
          {section.orderedList && (
            <ol className="legal-ordered-list">
              {section.orderedList.map((item, itemIndex) => (
                <li key={itemIndex}>{item}</li>
              ))}
            </ol>
          )}

          {/* Sub-sección */}
          {section.subSection && (
            <div className="legal-subsection">
              <h3>{section.subSection.title}</h3>
              {section.subSection.listItems && (
                <ul className="legal-list">
                  {section.subSection.listItems.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Email de contacto destacado */}
          {section.contactEmail && (
            <div className="legal-contact-email">
              <MailIcon />
              <a href={`mailto:${section.contactEmail}`}>{section.contactEmail}</a>
            </div>
          )}

          {/* Contenido adicional */}
          {section.additionalContent && (
            <div className="legal-additional-content">
              {section.additionalContent.split('\n\n').map((paragraph, pIndex) => (
                <p key={pIndex}>{paragraph}</p>
              ))}
            </div>
          )}

          {/* Sección de contacto especial */}
          {section.isContactSection && (
            <div className="legal-contact-box">
              <div className="contact-item">
                <MailIcon />
                <a href="mailto:contacto@wisby.legal">contacto@wisby.legal</a>
              </div>
            </div>
          )}

          {/* Info de contacto del titular */}
          {section.isContactInfo && (
            <div className="legal-company-info">
              <div className="company-info-item">
                <GlobeIcon />
                <a href="https://www.wisby.legal" target="_blank" rel="noopener noreferrer">www.wisby.legal</a>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="legal-page">
      <div className="legal-container">
        
        {/* Encabezado. */}
        <div className="legal-header">
          <button className="legal-back-button" onClick={handleGoBack}>
            <BackArrowIcon />
            <span>Volver al registro</span>
          </button>
        </div>

        {/* Card Principal */}
        <div className="legal-card">
          
          {/* Tabs */}
          <div className="legal-tabs">
            <button 
              className={`legal-tab-button ${activeTab === 'terms' ? 'active' : ''}`}
              onClick={() => setActiveTab('terms')}
            >
              <DocumentIcon />
              <span>Términos y Condiciones</span>
            </button>
            <button 
              className={`legal-tab-button ${activeTab === 'privacy' ? 'active' : ''}`}
              onClick={() => setActiveTab('privacy')}
            >
              <ShieldIcon />
              <span>Aviso de Privacidad</span>
            </button>
          </div>

          {/* Contenido */}
          <div className="legal-content">
            <div className="legal-content-header">
              <h1>{currentContent.title}</h1>
              <div className="legal-company-badge">
                <span>{currentContent.companyInfo.name}</span>
              </div>
              <span className="legal-update-date">{currentContent.lastUpdate}</span>
            </div>

            {/* Texto introductorio para Aviso de Privacidad */}
            {currentContent.introText && (
              <div className="legal-intro-text">
                <p>{currentContent.introText}</p>
              </div>
            )}

            <div className="legal-sections">
              {currentContent.sections.map((section, index) => renderSection(section, index))}
            </div>

            {/* Pie del contenido. */}
            <div className="legal-content-footer">
              <div className="legal-footer-info">
                <div className="legal-logo-small">WISBY</div>
                <p>Impulsora Mexicana de Derecho y Tecnología, S.A. de C.V.</p>
                <p className="legal-footer-contact">
                  <a href="mailto:contacto@wisby.legal">contacto@wisby.legal</a> | <a href="https://www.wisby.legal" target="_blank" rel="noopener noreferrer">www.wisby.legal</a>
                </p>
              </div>
              <button className="legal-accept-button" onClick={handleGoBack}>
                He leído y acepto
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
